import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import {
  Mail, Lock, User, Phone, Building2, Briefcase,
  Eye, EyeOff, Sun, Moon, CheckCircle2, RefreshCw, AlertCircle, Home
} from 'lucide-react';
import api from '../utils/api';
import toast from 'react-hot-toast';

const INDUSTRIES = [
  'Technology','Finance','Healthcare','Real Estate','Retail','Manufacturing',
  'Education','Marketing','Legal','Construction','Hospitality','E-commerce',
  'SaaS','Consulting','Other'
];

function Field({ k, label, placeholder, type='text', icon, value, onChange, error, showPw, onTogglePw }) {
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      <div className="input-group">
        {icon && React.cloneElement(icon, { size:14, className:'input-icon' })}
        <input
          className={`input${error ? ' input-error' : ''}`}
          style={icon ? { paddingLeft:38 } : {}}
          type={k === 'password' ? (showPw ? 'text' : 'password') : type}
          placeholder={placeholder}
          value={value}
          onChange={e => onChange(k, e.target.value)}
        />
        {k === 'password' && (
          <button type="button" className="input-icon-right" onClick={onTogglePw}>
            {showPw ? <EyeOff size={14}/> : <Eye size={14}/>}
          </button>
        )}
      </div>
      {error && <div className="form-error">{error}</div>}
    </div>
  );
}

export default function RegisterPage() {
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const [step,     setStep]   = useState(1);
  const [showPw,   setShowPw] = useState(false);
  const [loading,  setLoad]   = useState(false);
  const [error,    setError]  = useState('');
  const [errors,   setErrors] = useState({});
  const [userId,   setUserId] = useState('');
  const [email,    setEmail]  = useState('');
  const [otp,      setOtp]    = useState(['','','','','','']);
  const [resendCd, setResend] = useState(0);
  const refs = useRef([]);

  const [form, setForm] = useState({
    firstName:'', lastName:'', email:'', password:'', confirmPassword:'',
    company:'', jobTitle:'', industry:'', phone:'', city:'', country:'India',
  });

  const setField = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    setErrors(e => ({ ...e, [k]: '' }));
  };

  const v1 = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = 'Required';
    if (!form.lastName.trim())  e.lastName  = 'Required';
    if (!form.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) e.email = 'Valid email required';
    if (form.password.length < 6)               e.password        = 'Min 6 characters';
    if (form.password !== form.confirmPassword) e.confirmPassword = "Passwords don't match";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const v2 = () => {
    const e = {};
    if (!form.company.trim())  e.company  = 'Required';
    if (!form.jobTitle.trim()) e.jobTitle = 'Required';
    if (!form.industry)        e.industry = 'Required';
    if (!form.phone.trim())    e.phone    = 'Required';
    if (form.phone.trim() && !/^\+?[\d\s\-]{8,15}$/.test(form.phone)) e.phone = 'Valid phone required';
    if (!form.city.trim())     e.city     = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!v2()) return;
    setError(''); setLoad(true);
    try {
      const r = await api.post('/auth/register', form);
      setUserId(r.data.userId);
      setEmail(form.email);
      setStep(3);
      toast.success('OTP sent to your email!');
    } catch(err) {
      setError(err.response?.data?.message || 'Registration failed');
    } finally { setLoad(false); }
  };

  const otpChange = (i, val) => {
    if (!/^\d*$/.test(val)) return;
    const n = [...otp]; n[i] = val.slice(-1); setOtp(n);
    if (val && i < 5) refs.current[i+1]?.focus();
  };
  const otpKey = (i, e) => {
    if (e.key === 'Backspace' && !otp[i] && i > 0) refs.current[i-1]?.focus();
  };
  const otpPaste = e => {
    const p = e.clipboardData.getData('text').replace(/\D/g,'').slice(0,6);
    if (p.length === 6) { setOtp(p.split('')); refs.current[5]?.focus(); }
  };

  const verify = async () => {
    const code = otp.join('');
    if (code.length < 6) { setError('Enter the 6-digit OTP'); return; }
    setError(''); setLoad(true);
    try {
      const r = await api.post('/auth/verify-email', { userId, otp: code });

      if (!r.data.token || !r.data.user) {
        // Old backend without token — just go to login
        toast.success('Email verified! Please sign in.');
        navigate('/login');
        return;
      }

      // Store token in localStorage — api interceptor picks it up on next request
      localStorage.setItem('lr_token', r.data.token);

      // Store user in sessionStorage — AuthContext fast-path reads this on mount
      // This avoids the /auth/me round-trip and prevents any flash to /login
      sessionStorage.setItem('lr_user', JSON.stringify(r.data.user));

      toast.success('Welcome to LeadRadar! 🎉');

      // Hard redirect — cleanest way to re-initialize AuthContext with the new token
      // AuthContext.useEffect reads sessionStorage on mount → user set before Protected checks
      window.location.replace('/search');

    } catch(err) {
      setError(err.response?.data?.message || 'Invalid OTP. Please try again.');
      setLoad(false);
    }
  };

  const resend = async () => {
    if (resendCd > 0) return;
    try {
      await api.post('/auth/resend-verification', { userId });
      toast.success('New OTP sent!');
      setResend(30);
      const iv = setInterval(() => setResend(c => {
        if (c <= 1) { clearInterval(iv); return 0; }
        return c - 1;
      }), 1000);
    } catch { toast.error('Failed to resend'); }
  };

  const fp = (k, label, placeholder, type, icon) => ({
    k, label, placeholder, type, icon,
    value: form[k], onChange: setField,
    error: errors[k], showPw, onTogglePw: () => setShowPw(p => !p),
  });

  return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'var(--bg)', padding:'20px' }}>
      <div style={{ width:'100%', maxWidth: step===3 ? 420 : 520, background:'var(--surface)', borderRadius:16, padding:'40px 36px', boxShadow:'0 8px 40px rgba(0,0,0,.12)', position:'relative' }}>

        <div style={{ position:'absolute', top:16, right:16, display:'flex', gap:8 }}>
          <Link to="/" style={{ width:34, height:34, borderRadius:8, border:'1px solid var(--border)', background:'var(--bg3)', color:'var(--text2)', display:'flex', alignItems:'center', justifyContent:'center', textDecoration:'none' }} title="Home">
            <Home size={15}/>
          </Link>
          <button className="theme-toggle" onClick={toggleTheme}>
            {theme==='dark' ? <Sun size={16}/> : <Moon size={16}/>}
          </button>
        </div>

        <div style={{ marginBottom:24 }}>
          <div style={{ fontFamily:'var(--font-display)', fontWeight:800, fontSize:20, marginBottom:4 }}>
            📡 Lead<span style={{ color:'var(--accent)' }}>Radar</span>
          </div>
          {step < 3 && (
            <div style={{ display:'flex', gap:5, marginTop:12, marginBottom:16 }}>
              {[1,2].map(s => (
                <div key={s} style={{ flex:1, height:3, borderRadius:3, background:step>=s?'var(--accent)':'var(--border)', transition:'background .3s' }}/>
              ))}
            </div>
          )}
          <h2 style={{ fontFamily:'var(--font-display)', fontSize:22, fontWeight:800, marginBottom:4 }}>
            {step===1 && 'Create your account'}
            {step===2 && 'Tell us about you'}
            {step===3 && 'Verify your email'}
          </h2>
          <p style={{ color:'var(--text3)', fontSize:13.5 }}>
            {step===1 && 'Start finding leads for free'}
            {step===2 && 'Help us personalise your experience'}
            {step===3 && <>OTP sent to <strong>{email}</strong></>}
          </p>
        </div>

        {error && (
          <div className="alert alert-error" style={{ marginBottom:16, display:'flex', alignItems:'center', gap:8 }}>
            <AlertCircle size={15}/>{error}
          </div>
        )}

        {step===1 && (
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <div className="form-row">
              <Field {...fp('firstName','First Name','John','text',<User/>)}/>
              <Field {...fp('lastName','Last Name','Smith','text',<User/>)}/>
            </div>
            <Field {...fp('email','Email','john@company.com','email',<Mail/>)}/>
            <Field {...fp('password','Password','Min 6 characters','password',<Lock/>)}/>
            <Field {...fp('confirmPassword','Confirm Password','Repeat password','password',<Lock/>)}/>
            <button className="btn btn-primary btn-full" type="button" onClick={() => v1() && setStep(2)}>
              Continue →
            </button>
            <p style={{ textAlign:'center', fontSize:13.5, color:'var(--text3)' }}>
              Already have an account?{' '}
              <Link to="/login" style={{ color:'var(--accent)', fontWeight:600 }}>Sign in</Link>
            </p>
          </div>
        )}

        {step===2 && (
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <div className="form-row">
              <Field {...fp('company','Company','Acme Inc','text',<Building2/>)}/>
              <Field {...fp('jobTitle','Job Title','Sales Head','text',<Briefcase/>)}/>
            </div>
            <div className="form-group">
              <label className="form-label">Industry *</label>
              <select className={`select${errors.industry?' input-error':''}`} value={form.industry} onChange={e => setField('industry', e.target.value)}>
                <option value="">Select industry…</option>
                {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
              </select>
              {errors.industry && <div className="form-error">{errors.industry}</div>}
            </div>
            <Field {...fp('phone','Mobile Number','+91 98765 43210','text',<Phone/>)}/>
            <div className="form-row">
              <Field {...fp('city','City','Mumbai','text',null)}/>
              <Field {...fp('country','Country','India','text',null)}/>
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <button className="btn btn-ghost btn-full" type="button" onClick={() => { setStep(1); setError(''); }}>← Back</button>
              <button className="btn btn-primary btn-full" type="button" onClick={submit} disabled={loading}>
                {loading ? <><div className="spinner spinner-sm"/> Creating…</> : 'Create Account'}
              </button>
            </div>
          </div>
        )}

        {step===3 && (
          <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
            <div style={{ display:'flex', justifyContent:'center', gap:10 }} onPaste={otpPaste}>
              {otp.map((v,i) => (
                <input key={i} ref={el => refs.current[i] = el}
                  type="text" inputMode="numeric" maxLength={1} value={v}
                  onChange={e => otpChange(i, e.target.value)}
                  onKeyDown={e => otpKey(i, e)}
                  style={{
                    width:52, height:60, textAlign:'center', fontSize:26, fontWeight:800,
                    background:'var(--bg3)',
                    border:`2px solid ${v ? 'var(--accent)' : 'var(--border)'}`,
                    borderRadius:10, color:'var(--text)', outline:'none', transition:'border .15s',
                  }}
                />
              ))}
            </div>
            <button
              className="btn btn-primary btn-full btn-lg"
              type="button"
              onClick={verify}
              disabled={loading || otp.join('').length < 6}
            >
              {loading
                ? <><div className="spinner spinner-sm"/> Verifying…</>
                : <><CheckCircle2 size={16}/> Verify &amp; Go to Dashboard</>
              }
            </button>
            <div style={{ textAlign:'center', fontSize:13.5, color:'var(--text3)' }}>
              Didn't get it?{' '}
              <button type="button" onClick={resend} disabled={resendCd > 0}
                style={{ background:'none', border:'none', cursor:resendCd>0?'default':'pointer', color:resendCd>0?'var(--text3)':'var(--accent)', fontWeight:600, fontSize:13.5, padding:0, display:'inline-flex', alignItems:'center', gap:4 }}>
                {resendCd > 0 ? `Resend in ${resendCd}s` : <><RefreshCw size={12}/> Resend OTP</>}
              </button>
            </div>
            <div style={{ textAlign:'center', fontSize:12, color:'var(--text3)' }}>
              Check spam folder · OTP valid for 10 minutes
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// import React, { useState, useRef } from 'react';
// import { Link, useNavigate } from 'react-router-dom';
// import { useTheme } from '../context/ThemeContext';
// import { useAuth } from '../context/AuthContext';
// import { Mail, Lock, User, Phone, Building2, Briefcase, Eye, EyeOff, Sun, Moon, CheckCircle2, RefreshCw, AlertCircle, Home } from 'lucide-react';
// import api from '../utils/api';
// import toast from 'react-hot-toast';

// const INDUSTRIES = ['Technology','Finance','Healthcare','Real Estate','Retail','Manufacturing','Education','Marketing','Legal','Construction','Hospitality','E-commerce','SaaS','Consulting','Other'];

// // ── Field component defined OUTSIDE so React doesn't recreate it on each render ──
// // This fixes the focus-loss bug on keypress
// function Field({ k, label, placeholder, type='text', icon, value, onChange, error, showPw, onTogglePw }) {
//   return (
//     <div className="form-group">
//       <label className="form-label">{label}</label>
//       <div className="input-group">
//         {icon && React.cloneElement(icon, { size:14, className:'input-icon' })}
//         <input
//           className={`input${error ? ' input-error' : ''}`}
//           style={icon ? { paddingLeft:38 } : {}}
//           type={k === 'password' ? (showPw ? 'text' : 'password') : type}
//           placeholder={placeholder}
//           value={value}
//           onChange={e => onChange(k, e.target.value)}
//         />
//         {k === 'password' && (
//           <button type="button" className="input-icon-right" onClick={onTogglePw}>
//             {showPw ? <EyeOff size={14}/> : <Eye size={14}/>}
//           </button>
//         )}
//       </div>
//       {error && <div className="form-error">{error}</div>}
//     </div>
//   );
// }

// export default function RegisterPage() {
//   const { theme, toggleTheme } = useTheme();
//   const { loginDirect } = useAuth();
//   const navigate = useNavigate();

//   const [step,    setStep]    = useState(1);
//   const [showPw,  setShowPw]  = useState(false);
//   const [loading, setLoading] = useState(false);
//   const [error,   setError]   = useState('');
//   const [errors,  setErrors]  = useState({});
//   const [userId,  setUserId]  = useState('');
//   const [email,   setEmail]   = useState('');
//   const [otp,     setOtp]     = useState(['','','','','','']);
//   const [resendCd,setResend]  = useState(0);
//   const refs = useRef([]);

//   const [form, setForm] = useState({
//     firstName:'', lastName:'', email:'', password:'', confirmPassword:'',
//     company:'', jobTitle:'', industry:'', phone:'', city:'', country:'India',
//   });

//   const setField = (k, v) => { setForm(f => ({...f,[k]:v})); setErrors(e => ({...e,[k]:''})); };

//   const v1 = () => {
//     const e = {};
//     if (!form.firstName.trim()) e.firstName = 'Required';
//     if (!form.lastName.trim())  e.lastName  = 'Required';
//     if (!form.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) e.email = 'Valid email required';
//     if (form.password.length < 6) e.password = 'Min 6 characters';
//     if (form.password !== form.confirmPassword) e.confirmPassword = "Passwords don't match";
//     setErrors(e); return !Object.keys(e).length;
//   };

//   const v2 = () => {
//     const e = {};
//     if (!form.company.trim())  e.company  = 'Required';
//     if (!form.jobTitle.trim()) e.jobTitle = 'Required';
//     if (!form.industry)        e.industry = 'Required';
//     if (!form.phone.trim())    e.phone    = 'Required';
//     if (form.phone.trim() && !/^\+?[\d\s\-]{8,15}$/.test(form.phone)) e.phone = 'Valid phone required';
//     if (!form.city.trim())     e.city     = 'Required';
//     setErrors(e); return !Object.keys(e).length;
//   };

//   const submit = async () => {
//     if (!v2()) return;
//     setError(''); setLoading(true);
//     try {
//       const r = await api.post('/auth/register', form);
//       setUserId(r.data.userId);
//       setEmail(form.email);
//       setStep(3);
//       toast.success('OTP sent to your email!');
//     } catch(err) { setError(err.response?.data?.message || 'Registration failed'); }
//     finally { setLoading(false); }
//   };

//   const otpChange = (i, val) => {
//     if (!/^\d*$/.test(val)) return;
//     const n = [...otp]; n[i] = val.slice(-1); setOtp(n);
//     if (val && i < 5) refs.current[i+1]?.focus();
//   };
//   const otpKey = (i, e) => { if (e.key === 'Backspace' && !otp[i] && i > 0) refs.current[i-1]?.focus(); };
//   const otpPaste = e => {
//     const p = e.clipboardData.getData('text').replace(/\D/g,'').slice(0,6);
//     if (p.length === 6) { setOtp(p.split('')); refs.current[5]?.focus(); }
//   };

//   const verify = async () => {
//     const code = otp.join('');
//     if (code.length < 6) { setError('Enter the 6-digit OTP'); return; }
//     setError(''); setLoading(true);
//     try {
//       const r = await api.post('/auth/verify-email', { userId, otp: code });
//       // Auto-login directly — no need to visit /login page
//       if (r.data.token && r.data.user) {
//         loginDirect(r.data.token, r.data.user);
//       }
//       toast.success('Welcome to LeadRadar! 🎉');
//       navigate('/dashboard');
//     } catch(err) { setError(err.response?.data?.message || 'Invalid OTP'); }
//     finally { setLoading(false); }
//   };

//   const resend = async () => {
//     if (resendCd > 0) return;
//     try {
//       await api.post('/auth/resend-verification', { userId });
//       toast.success('New OTP sent!');
//       setResend(30);
//       const iv = setInterval(() => setResend(c => { if (c<=1){clearInterval(iv);return 0;} return c-1; }), 1000);
//     } catch { toast.error('Failed to resend'); }
//   };

//   // Shared Field props
//   const fp = (k, label, placeholder, type, icon) => ({
//     k, label, placeholder, type, icon,
//     value: form[k], onChange: setField,
//     error: errors[k], showPw, onTogglePw: () => setShowPw(p => !p),
//   });

//   return (
//     <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'var(--bg)', padding:'20px' }}>
//       <div style={{ width:'100%', maxWidth: step===3 ? 420 : 520, background:'var(--surface)', borderRadius:16, padding:'40px 36px', boxShadow:'0 8px 40px rgba(0,0,0,.12)', position:'relative' }}>

//         {/* Top controls */}
//         <div style={{ position:'absolute', top:16, right:16, display:'flex', gap:8 }}>
//           <Link to="/" style={{ width:34, height:34, borderRadius:8, border:'1px solid var(--border)', background:'var(--bg3)', color:'var(--text2)', display:'flex', alignItems:'center', justifyContent:'center', textDecoration:'none' }} title="Home">
//             <Home size={15}/>
//           </Link>
//           <button className="theme-toggle" onClick={toggleTheme}>
//             {theme==='dark' ? <Sun size={16}/> : <Moon size={16}/>}
//           </button>
//         </div>

//         {/* Logo */}
//         <div style={{ marginBottom:24 }}>
//           <div style={{ fontFamily:'var(--font-display)', fontWeight:800, fontSize:20, marginBottom:4 }}>
//             📡 Lead<span style={{ color:'var(--accent)' }}>Radar</span>
//           </div>

//           {step < 3 && (
//             <div style={{ display:'flex', gap:5, marginTop:12, marginBottom:16 }}>
//               {[1,2].map(s => (
//                 <div key={s} style={{ flex:1, height:3, borderRadius:3, background:step>=s?'var(--accent)':'var(--border)', transition:'background .3s' }}/>
//               ))}
//             </div>
//           )}

//           <h2 style={{ fontFamily:'var(--font-display)', fontSize:22, fontWeight:800, marginBottom:4 }}>
//             {step===1 && 'Create your account'}
//             {step===2 && 'Tell us about you'}
//             {step===3 && 'Verify your email'}
//           </h2>
//           <p style={{ color:'var(--text3)', fontSize:13.5 }}>
//             {step===1 && 'Start finding leads for free — no credit card'}
//             {step===2 && 'Help us personalise your experience'}
//             {step===3 && <>We sent a 6-digit OTP to <strong>{email}</strong></>}
//           </p>
//         </div>

//         {error && (
//           <div className="alert alert-error" style={{ marginBottom:16, display:'flex', alignItems:'center', gap:8 }}>
//             <AlertCircle size={15}/>{error}
//           </div>
//         )}

//         {/* ── STEP 1 ── */}
//         {step===1 && (
//           <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
//             <div className="form-row">
//               <Field {...fp('firstName','First Name','John','text',<User/>)}/>
//               <Field {...fp('lastName','Last Name','Smith','text',<User/>)}/>
//             </div>
//             <Field {...fp('email','Email','john@company.com','email',<Mail/>)}/>
//             <Field {...fp('password','Password','Min 6 characters','password',<Lock/>)}/>
//             <Field {...fp('confirmPassword','Confirm Password','Repeat password','password',<Lock/>)}/>
//             <button className="btn btn-primary btn-full" onClick={() => v1() && setStep(2)}>
//               Continue →
//             </button>
//             <p style={{ textAlign:'center', fontSize:13.5, color:'var(--text3)' }}>
//               Already have an account? <Link to="/login" style={{ color:'var(--accent)', fontWeight:600 }}>Sign in</Link>
//             </p>
//           </div>
//         )}

//         {/* ── STEP 2 ── */}
//         {step===2 && (
//           <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
//             <div className="form-row">
//               <Field {...fp('company','Company','Acme Inc','text',<Building2/>)}/>
//               <Field {...fp('jobTitle','Job Title','Sales Head','text',<Briefcase/>)}/>
//             </div>
//             <div className="form-group">
//               <label className="form-label">Industry *</label>
//               <select className={`select${errors.industry?' input-error':''}`} value={form.industry} onChange={e => setField('industry', e.target.value)}>
//                 <option value="">Select industry…</option>
//                 {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
//               </select>
//               {errors.industry && <div className="form-error">{errors.industry}</div>}
//             </div>
//             <Field {...fp('phone','Mobile Number *','+91 98765 43210','text',<Phone/>)}/>
//             <div className="form-row">
//               <Field {...fp('city','City *','Mumbai','text',null)}/>
//               <Field {...fp('country','Country','India','text',null)}/>
//             </div>
//             <div style={{ display:'flex', gap:10 }}>
//               <button className="btn btn-ghost btn-full" onClick={() => { setStep(1); setError(''); }}>← Back</button>
//               <button className="btn btn-primary btn-full" onClick={submit} disabled={loading}>
//                 {loading ? <><div className="spinner spinner-sm"/> Creating…</> : 'Create Account'}
//               </button>
//             </div>
//           </div>
//         )}

//         {/* ── STEP 3 — OTP ── */}
//         {step===3 && (
//           <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
//             <div style={{ display:'flex', justifyContent:'center', gap:10 }} onPaste={otpPaste}>
//               {otp.map((v,i) => (
//                 <input key={i} ref={el => refs.current[i] = el}
//                   type="text" inputMode="numeric" maxLength={1} value={v}
//                   onChange={e => otpChange(i, e.target.value)}
//                   onKeyDown={e => otpKey(i, e)}
//                   style={{ width:52, height:60, textAlign:'center', fontSize:26, fontWeight:800,
//                     background:'var(--bg3)', border:`2px solid ${v?'var(--accent)':'var(--border)'}`,
//                     borderRadius:10, color:'var(--text)', outline:'none', transition:'border .15s' }}
//                 />
//               ))}
//             </div>
//             <button className="btn btn-primary btn-full btn-lg" onClick={verify} disabled={loading || otp.join('').length < 6}>
//               {loading ? <><div className="spinner spinner-sm"/> Verifying…</> : <><CheckCircle2 size={16}/> Verify & Continue</>}
//             </button>
//             <div style={{ textAlign:'center', fontSize:13.5, color:'var(--text3)' }}>
//               Didn't get it?{' '}
//               <button onClick={resend} disabled={resendCd>0}
//                 style={{ background:'none', border:'none', cursor:resendCd>0?'default':'pointer',
//                   color:resendCd>0?'var(--text3)':'var(--accent)', fontWeight:600, fontSize:13.5, padding:0,
//                   display:'inline-flex', alignItems:'center', gap:4 }}>
//                 {resendCd>0 ? `Resend in ${resendCd}s` : <><RefreshCw size={12}/> Resend OTP</>}
//               </button>
//             </div>
//             <div style={{ textAlign:'center', fontSize:12.5, color:'var(--text3)' }}>
//               Check your spam folder · OTP valid for 10 minutes
//             </div>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// }